import { useYearlyCycle } from "./useYearlyCycle";
import { useMemo } from "react";
import { TaskSection, TasksLoadingSkeleton } from "./TaskSection";
import { useTasks, useTaskOccurrences } from "@dashboard/hooks/useTaskQuery";
import {
    pickLatestOccurrence,
    withOccurrence,
    type TaskWithOccurrence,
} from "@dashboard/utils/taskWithOccurrence";

export function YearlyTasks() {
    const { year } = useYearlyCycle();

    const { data: tasksResponse, isPending: isTasksPending } = useTasks({ annualCycle: true });
    const { data: occurrencesResponse, isPending: isOccurrencesPending } = useTaskOccurrences({
        take: 100,
    });

    const isPending = isTasksPending || isOccurrencesPending;

    const data = useMemo(() => {
        if (!tasksResponse?.data) return [];

        const occurrencesByTask = new Map<
            number,
            NonNullable<typeof occurrencesResponse>["data"][0][]
        >();
        if (occurrencesResponse?.data) {
            occurrencesResponse.data.forEach((occurrence) => {
                const list = occurrencesByTask.get(occurrence.taskId) ?? [];
                list.push(occurrence);
                occurrencesByTask.set(occurrence.taskId, list);
            });
        }

        return tasksResponse.data
            .map((backendTask) => {
                const occurrence = pickLatestOccurrence(occurrencesByTask.get(backendTask.id));
                return withOccurrence(backendTask, occurrence);
            })
            .filter((task) => task.dueDate.getFullYear() === year);
    }, [tasksResponse, occurrencesResponse, year]);

    const dataPerMonth = useMemo(() => {
        const map = new Map<number, TaskWithOccurrence[]>();
        [...Array(12).keys()].forEach((month) => map.set(month, []));
        data?.forEach((task) => {
            const month = task.dueDate.getMonth();
            map.get(month)?.push(task);
        });
        return map;
    }, [data]);

    return (
        <div className="flex h-full w-full flex-col gap-4 overflow-y-auto">
            {isPending && <TasksLoadingSkeleton />}
            {!isPending &&
                Array.from(dataPerMonth.entries()).map(([month, tasks]) => {
                    return <TaskSection key={month} month={month} tasks={tasks} />;
                })}
        </div>
    );
}
